/**
 * Pure, React-free settings route definitions.
 *
 * This module contains all settings path metadata without hooks, store access, or runtime dependencies.
 * Labels may reference static strings or resolver IDs for dynamic resolution.
 * Visibility gates may reference static booleans or gate IDs for runtime resolution.
 *
 * This allows both runtime hooks and extraction scripts to consume the same metadata source.
 */

import type { SentryTeamNames } from '@frontend/sentry-teams';
import settingsNavEn from '../../../../locales/en/settingsNav.json';

export type SettingsNavKey = keyof typeof settingsNavEn;

export const DYNAMIC_LOCATION_NAME_LABEL_ID = 'location-name-dynamic' as const;
export const DYNAMIC_NOTIFICATIONS_TYPE_LABEL_ID = 'notifications-type-dynamic' as const;

export type DynamicLabelId = typeof DYNAMIC_LOCATION_NAME_LABEL_ID | typeof DYNAMIC_NOTIFICATIONS_TYPE_LABEL_ID;

export type StaticLabel = Exclude<SettingsNavKey, DynamicLabelId>;
export type LabelValue = StaticLabel | DynamicLabelId;
export type SettingsLabelTranslator = (label: SettingsNavKey, options?: { ns?: 'settingsNav' }) => string;

export type StaticVisibility = boolean;
export type VisibilityGateId = string;
export type VisibilityValue = StaticVisibility | VisibilityGateId;

export interface SettingsPathDefinition {
  /** Human-readable label: either static string or ID to resolve dynamically */
  label: LabelValue;
  /** Team responsible for errors raised while rendering this route */
  team?: SentryTeamNames;
  /** (NOTE this is not currently used/rendered anywhere) Optional description of this settings page */
  description?: string;
  /** Optional alternative search labels/aliases */
  aliases?: string[];
  /** Visibility gate ID to resolve at runtime. If absent, route is always visible. */
  visibilityGateId?: VisibilityGateId;
}

/**
 * All settings paths organized by category for clarity.
 * Extracted from the runtime hook to be consumable by both React and Node contexts.
 */
export const SETTINGS_PATH_DEFINITIONS = {
  // Organization
  '/organization': {
    label: 'Organization Settings' /* AUTO-GENERATED */,
    team: 'users-infra',
    description: "User can manage the organization's settings from this page." /* AUTO-GENERATED */,
  },
  '/organization/users': {
    label: 'Organization Users' /* AUTO-GENERATED */,
    description: "User can manage the organization's users from this page." /* AUTO-GENERATED */,
    team: 'users-infra',
  },
  '/organization/phone-numbers': {
    label: 'Phone Numbers and Porting' /* AUTO-GENERATED */,
    team: 'fe-phone-config',
  },
  '/organization/phone-numbers/port-request': {
    label: 'Submit Port Request' /* AUTO-GENERATED */,
    team: 'fe-clm',
  },
  '/organization/billing-information': {
    label: 'Billing Information' /* AUTO-GENERATED */,
    description:
      "User can manage or view their organization's billing information from this page." /* AUTO-GENERATED */,
    team: 'fe-clm',
  },
  '/organization/invoice-history': {
    label: 'Invoice History' /* AUTO-GENERATED */,
    description: "User can view the organization's invoice history from this page." /* AUTO-GENERATED */,
    team: 'payments',
  },
  '/organization/invoice-history/:locationId': {
    label: 'location-name-dynamic',
  },
  '/organization/single-sign-on': {
    label: 'Single Sign On' /* AUTO-GENERATED */,
    team: 'users-infra',
    description:
      'User can configure or manage the Single Sign-On (SSO) settings for their organization from this page.' /* AUTO-GENERATED */,
  },
  '/organization/payment-methods': {
    label: 'Payment Methods' /* AUTO-GENERATED */,
    description:
      "Users can configure the payment methods that will be used to charge the weave's monthly/annual subscription charges." /* AUTO-GENERATED */,
    team: 'payments',
  },
  '/organization/data-sync': {
    label: 'Data Sync - Choose Location' /* AUTO-GENERATED */,
    description:
      'Configure data synchronization settings and choose locations for practice management integration.' /* AUTO-GENERATED */,
    team: 'fe-integrations',
  },
  '/organization/csv-upload': {
    label: 'Imported Contact Lists' /* AUTO-GENERATED */,
    description:
      'View and manage contact lists that have been imported from CSV files or other sources.' /* AUTO-GENERATED */,
    team: 'fe-integrations',
  },
  '/organization/csv-upload/map': {
    label: 'CSV Mapping' /* AUTO-GENERATED */,
  },
  '/organization/csv-upload/preview': {
    label: 'CSV Preview' /* AUTO-GENERATED */,
  },
  '/organization/csv-upload/file-mapping': {
    label: 'File Mapping' /* AUTO-GENERATED */,
  },
  '/organization/csv-upload/review-upload': {
    label: 'Review Your Upload' /* AUTO-GENERATED */,
  },
  '/organization/data-sync/location/:locationId': {
    label: 'Data Sync' /* AUTO-GENERATED */,
  },
  '/organization/data-sync/location/:locationId/source/:sourceId': {
    label: 'Customize Integration' /* AUTO-GENERATED */,
  },
  '/organization/data-sync/location/:locationId/new': {
    label: 'Add New Integration' /* AUTO-GENERATED */,
  },
  '/organization/business-verification': {
    label: 'Business Verification' /* AUTO-GENERATED */,
    description:
      'Complete business verification process for enhanced features and compliance requirements.' /* AUTO-GENERATED */,
    team: 'fe-messaging',
  },
  '/organization/messaging-compliance-registration': {
    label: 'Messaging Compliance Registration' /* AUTO-GENERATED */,
    description: 'Complete messaging compliance registration for 10DLC and enhanced SMS delivery.' /* AUTO-GENERATED */,
    team: 'fe-messaging',
    visibilityGateId: 'messaging-compliance-settings',
  },
  '/organization/users/setup-mobile-number/:nonce': {
    label: 'Mobile Number Verification' /* AUTO-GENERATED */,
  },
  '/organization/ip-restrictions': {
    label: 'IP Restrictions' /* AUTO-GENERATED */,
    description:
      'Configure IP address restrictions to enhance security and control system access.' /* AUTO-GENERATED */,
    team: 'users-infra',
  },
  '/organization/brand': {
    label: 'Brand Details' /* AUTO-GENERATED */,
    description:
      "Manage your practice's brand information including logos, colors, and visual identity." /* AUTO-GENERATED */,
    team: 'forms',
  },

  // Messaging
  '/messaging': {
    label: 'Messaging' /* AUTO-GENERATED */,
    description:
      'Primary messaging module overview. May contain shortcuts to inbox, archived messages, and templates.' /* AUTO-GENERATED */,
    team: 'fe-messaging',
  },
  '/messaging/templates': {
    label: 'Templates' /* AUTO-GENERATED */,
    description:
      'User can manage the messaging templates from this link, including creating new ones or editing existing ones.' /* AUTO-GENERATED */,
  },
  '/messaging/templates/create': {
    label: 'New Template' /* AUTO-GENERATED */,
  },
  '/messaging/templates/duplicate/:templateId': {
    label: 'Duplicate Template' /* AUTO-GENERATED */,
  },
  '/messaging/templates/edit/:templateId': {
    label: 'Edit Template' /* AUTO-GENERATED */,
  },
  '/messaging/templates/inbox-edit/:templateId': {
    label: 'Edit Manual Template' /* AUTO-GENERATED */,
  },
  '/messaging/templates/import': {
    label: 'Import Templates' /* AUTO-GENERATED */,
  },
  '/messaging/mark-confirmation-as-read': {
    label: 'Mark Confirmed' /* AUTO-GENERATED */,
    description:
      'User can manage settings related to how messaging confirmations are marked as read.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-customization-flag',
  },
  '/messaging/auto-messages': {
    label: 'Auto-Messages' /* AUTO-GENERATED */,
    description:
      'Create and manage automated messaging rules for appointment reminders and follow-ups.' /* AUTO-GENERATED */,
  },
  '/messaging/auto-messages/:ruleId': {
    label: 'Auto-Messages Rule' /* AUTO-GENERATED */,
  },
  '/messaging/auto-messages/:ruleId/composer': {
    label: 'Auto-Messages Composer' /* AUTO-GENERATED */,
  },
  '/messaging/auto-messages/:ruleId/editor': {
    label: 'Auto-Messages Editor' /* AUTO-GENERATED */,
  },
  '/messaging/auto-messages/:ruleId/new-email': {
    label: 'Auto-Messages Create Email' /* AUTO-GENERATED */,
  },
  '/messaging/auto-messages/:ruleId/templates': {
    label: 'Auto-Messages Templates' /* AUTO-GENERATED */,
  },
  '/messaging/auto-messages/:ruleId/reviews': {
    label: 'Auto-Messages Reviews Landing Create Page' /* AUTO-GENERATED */,
  },
  '/messaging/auto-messages/:ruleId/reviews/:landingPageTemplateId': {
    label: 'Auto-Messages Reviews Landing Edit Page' /* AUTO-GENERATED */,
  },
  '/messaging/message-queue': {
    label: 'Message Queue' /* AUTO-GENERATED */,
    description:
      'Monitor and manage the message delivery queue, including pending and failed messages.' /* AUTO-GENERATED */,
    visibilityGateId: 'message-queue-access',
  },
  '/messaging/record-history': {
    label: 'Record History' /* AUTO-GENERATED */,
    description:
      'View comprehensive message history and communication records for patients and contacts.' /* AUTO-GENERATED */,
    visibilityGateId: 'record-history-access',
  },
  '/messaging/record-history/:type/:recordId': {
    label: 'Record Details' /* AUTO-GENERATED */,
    visibilityGateId: 'record-history-access',
  },
  '/messaging/text-connect': {
    label: 'Text Connect Settings' /* AUTO-GENERATED */,
    description:
      'Configure Text Connect integration settings for enhanced messaging capabilities.' /* AUTO-GENERATED */,
    visibilityGateId: 'text-connect-access',
  },
  '/messaging/text-connect/:locationId': {
    label: 'location-name-dynamic',
    visibilityGateId: 'text-connect-access',
  },
  '/messaging/actionable-tab': {
    label: 'Utilize Actionable Tab' /* AUTO-GENERATED */,
    description:
      'Configure the actionable tab feature for processing and organizing incoming messages.' /* AUTO-GENERATED */,
  },
  '/messaging/appointment-confirmations': {
    label: 'Show Appointment Confirmations' /* AUTO-GENERATED */,
    description:
      'Configure how appointment confirmation messages are displayed and processed in the inbox.' /* AUTO-GENERATED */,
  },
  '/messaging/auto-messages-processing': {
    label: 'Auto-Messages Processing' /* AUTO-GENERATED */,
    description: 'Configure how automated messages are processed and handled by the system.' /* AUTO-GENERATED */,
  },
  '/messaging/bulk-messages-processing': {
    label: 'Bulk Text Messages Processing' /* AUTO-GENERATED */,
    description:
      'Configure how bulk text message campaigns are processed and handled by the system.' /* AUTO-GENERATED */,
  },
  '/messaging/show-unreplied-message-status': {
    label: 'Unreplied Message Status Visiblity' /* AUTO-GENERATED */,
    description:
      'Configure visibility settings for unreplied message status indicators in the inbox.' /* AUTO-GENERATED */,
  },

  // Personal
  '/personal': {
    label: 'Personal Settings' /* AUTO-GENERATED */,
    description: 'User can manage their personal settings from this page.' /* AUTO-GENERATED */,
    team: 'fe-ux-platform',
  },
  '/personal/phone/call-pop': {
    label: 'Call Pop' /* AUTO-GENERATED */,
    description:
      'User can configure their personal call pop settings for incoming calls from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-customization-flag',
  },
  '/personal/phone/device-connection': {
    label: 'Device Connection' /* AUTO-GENERATED */,
    description: 'Configure your personal phone device connection settings and preferences.' /* AUTO-GENERATED */,
    team: 'fe-phone-config',
    visibilityGateId: 'phone-customization-flag',
  },
  '/personal/phone/missed-call': {
    label: 'Missed Call' /* AUTO-GENERATED */,
    description:
      'User can change their personal settings for missed calls, likely including missed call notifications or automated responses, from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-customization-flag',
  },
  '/personal/notifications/:type': {
    label: 'notifications-type-dynamic',
  },
  '/personal/notifications/tag-notifications': {
    label: 'Tags' /* AUTO-GENERATED */,
    description:
      'User can manage and configure their personal notifications for specific tags from this page.' /* AUTO-GENERATED */,
  },
  '/personal/notifications/position-and-dismissal': {
    label: 'Position and Dismissal' /* AUTO-GENERATED */,
    description:
      'Configure notification preferences for desktop notification positioning and auto-dismissal.' /* AUTO-GENERATED */,
  },
  '/personal/notifications/sms-messages': {
    label: 'SMS Message Notification Preferences' /* AUTO-GENERATED */,
    description: 'Configure personal notification preferences for SMS message alerts and updates.' /* AUTO-GENERATED */,
  },
  '/personal/notifications/reputation': {
    label: 'Reviews Notification Preferences' /* AUTO-GENERATED */,
    description:
      'Configure personal notification preferences for new reviews and reputation updates.' /* AUTO-GENERATED */,
  },
  '/personal/notifications/ai-receptionist': {
    label: 'AI Receptionist' /* AUTO-GENERATED */,
    visibilityGateId: 'ai-receptionist-access',
  },
  '/personal/diagnostics': {
    label: 'Device & App Diagnostics' /* AUTO-GENERATED */,
    description:
      'User can self diagnose any issues related to the devices and apps.They can check any related System health alerts.' /* AUTO-GENERATED */,
  },
  '/personal/customize-app': {
    label: 'Customize Your App' /* AUTO-GENERATED */,
    description: 'Personalize your app appearance, theme, and user interface preferences.' /* AUTO-GENERATED */,
    visibilityGateId: 'app-customization-access',
  },
  '/personal/preferences/accessibility': {
    label: 'Accessibility' /* AUTO-GENERATED */,
    description:
      'Configure accessibility settings including font size, contrast, and assistive features.' /* AUTO-GENERATED */,
  },
  '/personal/email/fax': {
    label: 'Fax' /* AUTO-GENERATED */,
    description:
      'Configure personal email notification preferences for fax-related communications.' /* AUTO-GENERATED */,
    team: 'fe-messaging',
    visibilityGateId: 'fax-settings-email-updates',
  },
  '/personal/email/call-intelligence': {
    label: 'Call Intelligence Updates' /* AUTO-GENERATED */,
    description:
      'User can manage personal email settings for call intelligence features from this page.' /* AUTO-GENERATED */,
    team: 'analytics',
  },
  '/personal/version': {
    label: 'Version and Channel' /* AUTO-GENERATED */,
    description: 'View current app version information and configure update channel preferences.' /* AUTO-GENERATED */,
  },

  // Phone
  '/phone': {
    label: 'Phone' /* AUTO-GENERATED */,
    team: 'fe-phone-config',
  },
  '/phone/main': {
    label: 'Phone' /* AUTO-GENERATED */,
    description: 'Configure main phone system settings including general telephony preferences.' /* AUTO-GENERATED */,
  },
  '/phone/audio-library': {
    label: 'Audio Library' /* AUTO-GENERATED */,
    description:
      'User can manage the audio files and sound bites for the phone system from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-audio-library-access',
  },
  '/phone/call-blocking': {
    label: 'Call Blocking' /* AUTO-GENERATED */,
    description: 'User can manage call blocking settings from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-call-blocking-access',
  },
  '/phone/call-groups': {
    label: 'Call Groups' /* AUTO-GENERATED */,
    description:
      'User can manage phone call groups from this link, including creating, editing, or deleting groups and assigning members.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-call-groups-access',
  },
  '/phone/call-queues': {
    label: 'Call Queues' /* AUTO-GENERATED */,
    description: 'User can manage call queues settings for the phone system from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-call-queues-access',
  },
  '/phone/call-queues/:id': {
    label: 'Call Queue' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-call-queues-access',
  },
  '/phone/call-recording': {
    label: 'Call Recording' /* AUTO-GENERATED */,
    description:
      'User can manage the call recording settings for the phone system from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-call-recording-access',
  },
  '/phone/call-routes': {
    label: 'Call Routes' /* AUTO-GENERATED */,
    description:
      'User can manage and configure call routes for their phone system from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-call-routes-access',
  },
  '/phone/call-routes/:id': {
    label: 'Call Route' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-call-route-details-access',
  },
  '/phone/contact-directories': {
    label: 'Contact Directories' /* AUTO-GENERATED */,
    description: 'User can manage or configure phone contact directories from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-contact-directories-access',
  },
  '/phone/contact-directories/:id': {
    label: 'Contact Directories' /* AUTO-GENERATED */,
  },
  '/phone/departments': {
    label: 'Departments' /* AUTO-GENERATED */,
    description:
      'User can manage phone system departments from this link, including adding, editing, or viewing department settings.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-departments-access',
  },
  '/phone/departments/:id': {
    label: 'Department' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-departments-access',
  },
  '/phone/devices': {
    label: 'Device Settings' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-device-settings-access',
  },
  '/phone/line-keys': {
    label: 'Line Keys' /* AUTO-GENERATED */,
    description:
      'User can manage or configure the line keys for their phone system from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-line-keys-access',
  },
  '/phone/mainline': {
    label: 'Mainline' /* AUTO-GENERATED */,
    description: 'User can manage the settings for the main phone line from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-main-line-access',
  },
  '/phone/overrides': {
    label: 'Overrides' /* AUTO-GENERATED */,
    description:
      'User can manage specific phone system overrides or exceptions from this link, such as special routing rules or temporary changes to phone behavior.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-overrides-access',
  },
  '/phone/phone-hours': {
    label: 'Phone Hours' /* AUTO-GENERATED */,
    description: 'User can manage the phone operating hours from this page.' /* AUTO-GENERATED */,
    aliases: ['Office Hours'],
    visibilityGateId: 'phone-office-hours-access',
  },
  '/phone/phone-system-history': {
    label: 'Phone System History' /* AUTO-GENERATED */,
    description: 'User can view the history for their phone system from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-system-history-access',
  },
  '/phone/phone-tree-quick-actions': {
    label: 'Phone Tree Quick Actions' /* AUTO-GENERATED */,
    description:
      'User can manage or configure quick actions for the phone tree (IVR) system from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-tree-quick-actions-access',
  },
  '/phone/phone-tree-quick-actions/:id': {
    label: 'Phone Tree' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-tree-quick-actions-access',
  },
  '/phone/voicemail-box': {
    label: 'Voicemail Boxes' /* AUTO-GENERATED */,
    description: 'User can manage their phone voicemail boxes from this page.' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-voicemail-boxes-access',
  },
  '/phone/voicemail-box/:id': {
    label: 'Voicemail Box' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-voicemail-boxes-access',
  },
  '/phone/hold-music': {
    label: 'Hold Music' /* AUTO-GENERATED */,
    description: 'User can manage the hold music from this link which includes changing the music' /* AUTO-GENERATED */,
    visibilityGateId: 'phone-hold-music-access',
  },

  // Tags
  '/tags': {
    label: 'Tags' /* AUTO-GENERATED */,
    description:
      'User can manage the tags from this link, which likely includes creating, editing, or deleting tags used for categorizing contacts or communications.' /* AUTO-GENERATED */,
    team: 'fe-messaging',
  },
  '/tags/create': {
    label: 'New Tag' /* AUTO-GENERATED */,
    description: 'User can create new tags from this page within the settings section.' /* AUTO-GENERATED */,
  },
  '/tags/edit/:tagId': {
    label: 'Edit Tag' /* AUTO-GENERATED */,
  },
  '/tags/duplicate/:tagId': {
    label: 'Duplicate Tag' /* AUTO-GENERATED */,
  },
  '/tags/quick-create': {
    label: 'New Tag' /* AUTO-GENERATED */,
    description: 'User can quickly create new tags from this link within the settings.' /* AUTO-GENERATED */,
  },

  // Digital Forms
  '/digital-forms': {
    label: 'Forms' /* AUTO-GENERATED */,
    description:
      'Shortcut to the digital forms module. View, create, or manage practice forms in a centralized place.' /* AUTO-GENERATED */,
    team: 'forms',
  },
  '/digital-forms/customize-kiosk': {
    label: 'Manage Forms List View For Patients' /* AUTO-GENERATED */,
    description:
      'User can customize the settings and appearance of the digital forms kiosk from this page.' /* AUTO-GENERATED */,
  },
  '/digital-forms/medical-history': {
    label: 'Patient Medical History' /* AUTO-GENERATED */,
    description:
      'User can setup medical conditions here and then use it consistently in the form builder. They are also sent back to PMS.' /* AUTO-GENERATED */,
  },
  '/digital-forms/privacy-policy': {
    label: 'Privacy Policy' /* AUTO-GENERATED */,
    description:
      'User can manage the privacy policy settings related to digital forms from this page.' /* AUTO-GENERATED */,
  },
  '/digital-forms/provider-mapping': {
    label: 'Provider Mapping' /* AUTO-GENERATED */,
    description:
      'User can associate Weave Users with Providers from your PMS/EHR.This allows users to view submitted form responses and manage what is written back to the PMS' /* AUTO-GENERATED */,
  },
  '/digital-forms/terms-and-conditions': {
    label: 'Terms & Conditions' /* AUTO-GENERATED */,
    description:
      'User can Insert terms and conditions that will appear as a clickable link in the footer of all your forms pages.' /* AUTO-GENERATED */,
  },
  '/digital-forms/writebacks': {
    label: 'Writeback Settings' /* AUTO-GENERATED */,
    description:
      'User can configure writeback settings for digital forms, likely managing how submitted form data updates other systems.' /* AUTO-GENERATED */,
  },
  '/digital-forms/insurance-verification': {
    label: 'Insurance Fields' /* AUTO-GENERATED */,
    description: 'User can configure automated verification of insurance fields on digital forms.' /* AUTO-GENERATED */,
  },

  // Payments
  '/payments': {
    label: 'Payments' /* AUTO-GENERATED */,
    description:
      'Main payments overview page. Access invoices, statements, settings, and payment-related features.' /* AUTO-GENERATED */,
    team: 'payments',
  },
  '/payments/general-settings': {
    label: 'General Settings' /* AUTO-GENERATED */,
    description: 'User can manage the general settings related to payments from this page.' /* AUTO-GENERATED */,
  },
  '/payments/online-bill-pay': {
    label: 'Online Bill Pay' /* AUTO-GENERATED */,
    description: 'User can manage the settings for online bill payments from this page.' /* AUTO-GENERATED */,
  },
  '/payments/payment-methods': {
    label: 'Payment Methods' /* AUTO-GENERATED */,
    description: 'User can manage payment methods from this page.' /* AUTO-GENERATED */,
  },
  '/payments/public-business-information': {
    label: 'Public Business Information' /* AUTO-GENERATED */,
  },
  '/payments/terminals': {
    label: 'Terminal Settings' /* AUTO-GENERATED */,
  },
  '/payments/account-info': {
    label: 'Account Info and Status' /* AUTO-GENERATED */,
    description: 'View payment processor account information, status, and configuration details.' /* AUTO-GENERATED */,
  },
  '/payments/justifi-settings': {
    label: 'JustiFi Setup' /* AUTO-GENERATED */,
    description: 'Configure JustiFi payment processing integration and merchant account settings.' /* AUTO-GENERATED */,
  },
  '/payments/sunbit': {
    label: 'Sunbit' /* AUTO-GENERATED */,
    description:
      'Configure Sunbit financing integration for patient payment options and financing solutions.' /* AUTO-GENERATED */,
  },
  '/payments/care-credit': {
    label: 'Account Info and Status' /* AUTO-GENERATED */,
    description: 'View payment processor account information, status, and configuration details.' /* AUTO-GENERATED */,
  },

  // Email
  '/email': {
    label: 'Email' /* AUTO-GENERATED */,
    description:
      'Email marketing settings. Manage email addresses and domains used with email marketing.' /* AUTO-GENERATED */,
    team: 'fe-messaging',
    visibilityGateId: 'domain-verify-settings',
  },

  // Fax
  '/fax': {
    label: 'Fax' /* AUTO-GENERATED */,
    description:
      'Primary fax module overview, providing quick access to inbox, drafts, and fax configuration.' /* AUTO-GENERATED */,
    team: 'fe-messaging',
  },
  '/fax/history': {
    label: 'Fax History' /* AUTO-GENERATED */,
    description:
      'View historical records of sent and received faxes with delivery status and timestamps.' /* AUTO-GENERATED */,
  },

  // Reviews/Social
  '/reviews': {
    label: 'Social' /* AUTO-GENERATED */,
    description: 'User can manage settings related to reviews from this page.' /* AUTO-GENERATED */,
    team: 'fe-messaging',
  },

  // Schedule
  '/schedule': {
    label: 'Schedule' /* AUTO-GENERATED */,
    description: 'Primary scheduling hub, linking to calendar, appointment' /* AUTO-GENERATED */,
    team: 'schedule',
  },
  '/schedule/appointment-types': {
    label: 'Appointment Types' /* AUTO-GENERATED */,
    description:
      'Create and manage different types of appointments with specific durations and requirements.' /* AUTO-GENERATED */,
  },
  '/schedule/appointment-types/:locationId': {
    label: 'location-name-dynamic',
  },
  '/schedule/message-templates': {
    label: 'Message Templates' /* AUTO-GENERATED */,
    description:
      'Create and manage message templates for appointment confirmations, reminders, and communications.' /* AUTO-GENERATED */,
  },
  '/schedule/appointment-hours': {
    label: 'Appointment Hours' /* AUTO-GENERATED */,
    description: 'Configure available appointment hours and scheduling windows for your practice.' /* AUTO-GENERATED */,
  },
  '/schedule/message-templates/appointment-confirmed/:locationId': {
    label: 'Appointment Confirmed' /* AUTO-GENERATED */,
  },
  '/schedule/message-templates/request-received/:locationId': {
    label: 'Request Received' /* AUTO-GENERATED */,
  },
  '/schedule/custom-fields': {
    label: 'Custom Fields' /* AUTO-GENERATED */,
    description:
      'Create and manage custom fields for capturing additional information during appointment booking.' /* AUTO-GENERATED */,
  },
  '/schedule/manage-providers': {
    label: 'Manage Providers' /* AUTO-GENERATED */,
    description: 'Add, edit, and manage healthcare providers and their scheduling availability.' /* AUTO-GENERATED */,
  },
  '/schedule/manage-providers/edit/:providerId': {
    label: 'Edit Provider' /* AUTO-GENERATED */,
  },
  '/schedule/manage-providers/:locationId/edit/:providerId': {
    label: 'Edit Provider' /* AUTO-GENERATED */,
  },
  '/schedule/manage-providers/:locationId': {
    label: 'location-name-dynamic',
  },
  '/schedule/booking-site-setup': {
    label: 'Booking Site Setup' /* AUTO-GENERATED */,
    description:
      'Configure your online booking site appearance, settings, and patient self-scheduling options.' /* AUTO-GENERATED */,
  },
  '/schedule/schedule-definition-templates': {
    label: 'Schedule Definitions (Templates)' /* AUTO-GENERATED */,
    description: 'Manage your treatment rooms and chairs for optimal scheduling and operations.' /* AUTO-GENERATED */,
  },
  '/schedule/schedule-definition-templates/:locationId': {
    label: 'location-name-dynamic',
  },
  '/schedule/services': {
    label: 'Services' /* AUTO-GENERATED */,
    description:
      'Define what services you offer. Visit Availability Rules to control when, where and how they’re available.' /* AUTO-GENERATED */,
    visibilityGateId: 'schedule-services-access',
  },
  '/schedule/services/:locationId': {
    label: 'location-name-dynamic',
    visibilityGateId: 'schedule-services-access',
  },
  '/schedule/availability': {
    label: 'Availability' /* AUTO-GENERATED */,
    description: 'Manange your availability by setting up rules and exceptions' /* AUTO-GENERATED */,
    visibilityGateId: 'schedule-availability-access',
  },
  '/schedule/availability/:locationId': {
    label: 'location-name-dynamic',
    visibilityGateId: 'schedule-availability-access',
  },
  '/schedule/operatories': {
    label: 'Operatories' /* AUTO-GENERATED */,
    description:
      'Configure operatory rooms and treatment spaces for appointment scheduling and resource management.' /* AUTO-GENERATED */,
  },
  '/schedule/operatories/:locationId': {
    label: 'location-name-dynamic',
  },
  '/schedule/operatories/edit/:operatoryId': {
    label: 'Edit Operatory' /* AUTO-GENERATED */,
  },
  '/schedule/operatories/:locationId/edit/:operatoryId': {
    label: 'Edit Operatory' /* AUTO-GENERATED */,
  },
  '/schedule/cancellation-policy': {
    label: 'Cancellation Policy' /* AUTO-GENERATED */,
    description:
      'Set the rules for how cancellations and modifications are handled, both on your online booking page and when automated systems are managing scheduling on your behalf.' /* AUTO-GENERATED */,
  },
  '/schedule/cancellation-policy/:locationId': {
    label: 'location-name-dynamic',
  },

  // Subscriptions
  '/subscriptions': {
    label: 'Subscriptions' /* AUTO-GENERATED */,
    description: 'View and manage your current subscriptions, billing, and feature access levels.' /* AUTO-GENERATED */,
    team: 'fe-clm',
  },
  '/subscriptions/feature/:id': {
    label: 'Feature Subscriptions' /* AUTO-GENERATED */,
  },
  '/subscriptions/manage-subscription': {
    label: 'Manage Subscription' /* AUTO-GENERATED */,
    description: 'Modify your subscription plan, add features, and manage billing preferences.' /* AUTO-GENERATED */,
  },

  // Brand
  '/brand': {
    label: 'Brand' /* AUTO-GENERATED */,
    description:
      'User can manage or configure branding settings for their Weave account or services from this page.' /* AUTO-GENERATED */,
    team: 'fe-messaging',
  },

  // Image Gallery
  '/image-gallery': {
    label: 'Image Gallery' /* AUTO-GENERATED */,
    description:
      'Manage practice images and media assets used across various communications and forms.' /* AUTO-GENERATED */,
    team: 'fe-ux-platform',
    visibilityGateId: 'image-gallery-access',
  },

  // Insurance
  '/insurance': {
    label: 'Insurance' /* AUTO-GENERATED */,
    description: 'Manage insurance verification settings and payer connection configurations.' /* AUTO-GENERATED */,
    team: 'fe-rcm',
  },
  '/insurance/automatic-verification': {
    label: 'Automatic Verification' /* AUTO-GENERATED */,
    description: 'Configure automatic insurance verification settings and eligibility checking.' /* AUTO-GENERATED */,
  },
  '/insurance/automatic-verification/:locationId': {
    label: 'location-name-dynamic',
  },
  '/insurance/payer-connection-management': {
    label: 'Payer Connection Management' /* AUTO-GENERATED */,
    description:
      'Manage connections to insurance payers and configure eligibility verification settings.' /* AUTO-GENERATED */,
  },
  '/insurance/payer-connection-management/:locationId': {
    label: 'location-name-dynamic',
  },
  '/insurance/cdt-code-management': {
    label: 'CDT Code Management' /* AUTO-GENERATED */,
    description: 'Manage your CDT code settings for faster access in Insurance Reports.' /* AUTO-GENERATED */,
  },
  '/insurance/cdt-code-management/:locationId': {
    label: 'location-name-dynamic',
  },

  // Weave Assistant / AI Configuration
  '/weave-assistant': {
    label: 'Weave Assistant' /* AUTO-GENERATED */,
    description: 'User can manage the settings for the Weave Assistant from this page.' /* AUTO-GENERATED */,
    team: 'fe-messaging',
    visibilityGateId: 'weave-assistant-settings',
  },
  '/weave-assistant/assist-with-calls': {
    label: 'Assist With Calls' /* AUTO-GENERATED */,
    description:
      'Configure Weave Assistant to help with call handling, routing, and customer service tasks.' /* AUTO-GENERATED */,
    visibilityGateId: 'weave-assistant-settings',
  },
  '/weave-assistant/assist-with-text-messages': {
    label: 'Assist With Text Messages' /* AUTO-GENERATED */,
    description:
      'Configure Weave Assistant to help with text message responses and patient communications.' /* AUTO-GENERATED */,
    visibilityGateId: 'weave-assistant-settings',
  },
  '/weave-assistant/general-details': {
    label: 'General Details' /* AUTO-GENERATED */,
    description:
      'Configure general practice information and details for Weave Assistant knowledge base.' /* AUTO-GENERATED */,
    visibilityGateId: 'weave-assistant-settings',
  },
  '/weave-assistant/office-amenities': {
    label: 'Office Amenities' /* AUTO-GENERATED */,
    description:
      'Configure office amenities and facility information for Weave Assistant to share with patients.' /* AUTO-GENERATED */,
    visibilityGateId: 'weave-assistant-settings',
  },
  '/weave-assistant/payment-and-insurance': {
    label: 'Payment & Insurance' /* AUTO-GENERATED */,
    description:
      'Configure payment and insurance information for Weave Assistant to help patients with billing inquiries.' /* AUTO-GENERATED */,
    visibilityGateId: 'weave-assistant-settings',
  },
  '/weave-assistant/schedule-and-availability': {
    label: 'Schedule & Availability' /* AUTO-GENERATED */,
    description:
      'Configure scheduling and availability information for Weave Assistant to help with appointment booking.' /* AUTO-GENERATED */,
    visibilityGateId: 'weave-assistant-settings',
  },
  '/weave-assistant/advanced-training': {
    label: 'Advanced Training' /* AUTO-GENERATED */,
    description:
      'Provide advanced training data and custom instructions to enhance Weave Assistant performance.' /* AUTO-GENERATED */,
    visibilityGateId: 'weave-assistant-settings',
  },
  '/weave-assistant/weave-assistant': {
    label: 'Weave Assistant' /* AUTO-GENERATED */,
    description:
      'Main Weave Assistant configuration and overview of AI capabilities and training status.' /* AUTO-GENERATED */,
  },
  '/ai-configuration': {
    label: 'AI Settings' /* AUTO-GENERATED */,
    description: 'Manage and configure your AI-powered tools and features.' /* AUTO-GENERATED */,
    team: 'frontend',
  },
  '/ai-configuration/ai-receptionist': {
    label: 'AI Receptionist' /* AUTO-GENERATED */,
    description:
      'Configure and train your AI Receptionist to better understand your business & goals.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/ai-receptionist': {
    label: 'AI Receptionist' /* AUTO-GENERATED */,
    description:
      'Main Weave Assistant configuration and overview of AI capabilities and training status.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/business-preferences': {
    label: 'Business Preferences' /* AUTO-GENERATED */,
    description: 'Let your AI Receptionist get to know your business.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/online-booking': {
    label: 'Online Booking' /* AUTO-GENERATED */,
    description: 'Allow patients to book services from your website.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/web-to-text': {
    label: 'Web to Text' /* AUTO-GENERATED */,
    description: 'Allow patients to text your practice from your website.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/business-preferences/amenities': {
    label: 'Amenities' /* AUTO-GENERATED */,
    description: 'Features and experiences offered to patients.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/business-preferences/before-after-appointment': {
    label: 'Before & After Appointment' /* AUTO-GENERATED */,
    description: 'Pre & post procedure instructions.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/business-preferences/business-profile': {
    label: 'Business Profile' /* AUTO-GENERATED */,
    description: 'Let your AI Receptionist get to know your business.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/business-preferences/hidden-preferences': {
    label: 'Hidden Preferences' /* AUTO-GENERATED */,
    description: 'Provide additional preferences for scheduling, amenities, and FAQ responses.' /* AUTO-GENERATED */,
    visibilityGateId: 'weave-user-only',
  },
  '/ai-configuration/ai-receptionist/business-preferences/insurance': {
    label: 'Insurance' /* AUTO-GENERATED */,
    description: 'Accepting insurance from patients.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/business-preferences/missed-calls': {
    label: 'Missed Calls' /* AUTO-GENERATED */,
    description: 'How should your AI Receptionist respond to missed calls?' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/business-preferences/notifications': {
    label: 'Notifications' /* AUTO-GENERATED */,
    description: 'Notifications to your practice.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/business-preferences/patient-billing': {
    label: 'Patient Billing' /* AUTO-GENERATED */,
    description: 'Accepting payments from patients.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/business-preferences/pharmacy-records': {
    label: 'Pharmacy & Records' /* AUTO-GENERATED */,
    description: 'Prescriptions, x-rays, and more.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/business-preferences/scheduling': {
    label: 'Scheduling' /* AUTO-GENERATED */,
    description: 'Booking appointments for your practice.' /* AUTO-GENERATED */,
  },

  // Analytics
  '/analytics': {
    label: 'Analytics' /* AUTO-GENERATED */,
    description:
      'High-level analytics dashboard providing snapshots of calls, messages, appointments, and other KPIs.' /* AUTO-GENERATED */,
    team: 'analytics',
  },
  '/digital-forms/pdf-layout': {
    label: 'PDF Layout' /* AUTO-GENERATED */,
    description:
      "Control how your forms look as a PDF. Your chosen layout will apply to all forms, whether you're printing a blank form or reviewing a completed one synced to your PMS." /* AUTO-GENERATED */,
  },
  '/messaging/auto-message-inbox-visibility': {
    label: 'Auto-Message Inbox Visibility' /* AUTO-GENERATED */,
    description:
      'Control which automated messages are visible in the main inbox versus processed silently.' /* AUTO-GENERATED */,
  },
  '/messaging/mute-inbox': {
    label: 'Mute Inbox' /* AUTO-GENERATED */,
    description: 'Mute inbox activity for workspace-associated locations.' /* AUTO-GENERATED */,
  },
  '/organization/workspaces/:workspaceId': {
    label: 'Workspace' /* AUTO-GENERATED */,
    description:
      'Detail page for a single workspace — view and manage assigned locations and users.' /* AUTO-GENERATED */,
  },
  '/organization/workspaces': {
    label: 'Workspaces' /* AUTO-GENERATED */,
    description: 'User can manage workspaces to group locations together.' /* AUTO-GENERATED */,
    team: 'fe-messaging',
  },
  '/subscriptions/feature/ai-receptionist': {
    label: 'AI Receptionist Subscription' /* AUTO-GENERATED */,
  },
  '/subscriptions/feature/call-intelligence': {
    label: 'Call Intelligence Subscription' /* AUTO-GENERATED */,
  },
  '/personal/preferences/patient-identity-display': {
    label: 'Patient Identity Display' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/instructions/messaging-voice': {
    label: 'Messaging & Voice Instruction' /* AUTO-GENERATED */,
    description: 'Configure how AI Receptionist should handle and assist you with communications.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/instructions/scheduling': {
    label: 'Scheduling Instruction' /* AUTO-GENERATED */,
    description: 'Configure how AI Receptionist should handle and assist you with scheduling.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/knowledge-base/business': {
    label: 'Business Knowledge' /* AUTO-GENERATED */,
    description:
      'This is what the AI Receptionist can share about your business during conversations.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/knowledge-base/custom': {
    label: 'Custom Knowledge' /* AUTO-GENERATED */,
    description:
      'Add custom knowledge if you would like AI Receptionist to be able to share unique details about your business.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/knowledge-base/payments': {
    label: 'Payment & Insurance Knowledge' /* AUTO-GENERATED */,
    description:
      'This is what the AI Receptionist can share about payments & insurance during conversations.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/ai-receptionist/knowledge-base/schedule': {
    label: 'Schedule Knowledge' /* AUTO-GENERATED */,
    description:
      'This is what the AI Receptionist can share about your schedule during conversations.' /* AUTO-GENERATED */,
  },
  '/organization/device-orders': {
    label: 'Device Orders' /* AUTO-GENERATED */,
    description:
      'View and manage device orders made through Weave. Select your preferred location to continue.' /* AUTO-GENERATED */,
    team: 'fe-clm',
  },
  '/organization/device-orders/:locationId': {
    label: 'Device Orders' /* AUTO-GENERATED */,
    description: 'View and manage device orders made through Weave.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/call-intelligence': {
    label: 'Call Intelligence Updates' /* AUTO-GENERATED */,
    team: 'analytics',
  },
  '/organization/sync-management': {
    label: 'Sync Management' /* AUTO-GENERATED */,
    team: 'analytics',
  },
  '/personal/profile/:id': {
    label: 'Personal Settings' /* AUTO-GENERATED */,
    team: 'users-infra',
  },
  '/phone/e911': {
    label: 'Emergency Calling (E911)' /* AUTO-GENERATED */,
    description:
      'User can manage or configure E911 (Enhanced 911) settings for their phone system from this page.' /* AUTO-GENERATED */,
  },
  '/ai-configuration/main': {
    label: 'AI Settings' /* AUTO-GENERATED */,
    description: 'Manage and configure your AI-powered tools and features.' /* AUTO-GENERATED */,
  },
  '/task-center': {
    label: 'Task Center' /* AUTO-GENERATED */,
    description:
      'User can manage the task center settings from this link, which likely includes configuring tasks, notifications, and other related settings for the task management system.' /* AUTO-GENERATED */,
  },
  '/task-center/task-archival': {
    label: 'Task Archival' /* AUTO-GENERATED */,
    description: 'Task Archival settings' /* AUTO-GENERATED */,
  },
} as const satisfies Record<string, SettingsPathDefinition>;

export type SettingsPath = keyof typeof SETTINGS_PATH_DEFINITIONS;
export type ExtractableSettingsPathDefinition = SettingsPathDefinition & { path: SettingsPath };

export const DEFAULT_SETTINGS_ROUTE_TEAM: SentryTeamNames = 'frontend-ops';

const normalizeSettingsPath = (path: string): string[] => {
  const pathname = path
    .split(/[?#]/, 1)[0]
    .replace(/^\/?settings(?=\/|$)/, '')
    .replace(/^\/+|\/+$/g, '');
  return pathname ? pathname.split('/') : [];
};

const isMatchingRoutePrefix = (patternSegments: string[], pathSegments: string[]): boolean => {
  return (
    patternSegments.length <= pathSegments.length &&
    patternSegments.every((segment, index) => segment.startsWith(':') || segment === pathSegments[index])
  );
};

const SETTINGS_ROUTE_TEAMS = (
  Object.entries(SETTINGS_PATH_DEFINITIONS) as [SettingsPath, SettingsPathDefinition][]
).flatMap(([routePattern, definition]) =>
  definition.team ? [{ patternSegments: normalizeSettingsPath(routePattern), team: definition.team }] : []
);

export function resolveSettingsRouteTeam(path: string): SentryTeamNames {
  const pathSegments = normalizeSettingsPath(path);
  let resolvedTeam = DEFAULT_SETTINGS_ROUTE_TEAM;
  let resolvedDepth = 0;

  for (const { patternSegments, team } of SETTINGS_ROUTE_TEAMS) {
    const routeDepth = patternSegments.length;
    if (routeDepth > resolvedDepth && isMatchingRoutePrefix(patternSegments, pathSegments)) {
      resolvedTeam = team;
      resolvedDepth = routeDepth;
    }
  }

  return resolvedTeam;
}

/**
 * Helper to find a definition by path
 */
export function findSettingsDefinitionByPath(path: string): SettingsPathDefinition | undefined {
  if (path in SETTINGS_PATH_DEFINITIONS) {
    return SETTINGS_PATH_DEFINITIONS[path as SettingsPath];
  }

  return undefined;
}

/**
 * Helper to get all extractable definitions (those that should be included in export output)
 *
 * Excludes:
 * - Routes with path parameters (e.g., ':id', ':locationId', ':type') since these are
 *   dynamic and resolve to different destinations based on runtime context
 * - Dynamic label routes that will be resolved at runtime
 */
export function getExtractableSettingsDefinitions(): ExtractableSettingsPathDefinition[] {
  return (Object.entries(SETTINGS_PATH_DEFINITIONS) as [SettingsPath, SettingsPathDefinition][]).flatMap(
    ([path, definition]) => {
      // Exclude parameterized routes (contain ':' in path)
      const hasPathParams = path.includes(':');
      // Exclude routes with dynamic label resolution
      const hasDynamicLabel =
        definition.label === DYNAMIC_LOCATION_NAME_LABEL_ID || definition.label === DYNAMIC_NOTIFICATIONS_TYPE_LABEL_ID;

      if (hasPathParams || hasDynamicLabel) {
        return [];
      }

      return [{ path, ...definition }];
    }
  );
}
